import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Header, Hero } from './components';
import { WebMCPProvider } from './components/WebMCP';
import { ThemeProvider, ThemeModeProvider } from './contexts';
import { analytics } from './utils/analytics';
import { captureUTMParameters } from './utils/analytics/utmCapture';
import { trackSectionViews } from './utils/analytics/scrollTracking';
import './styles/main.scss';

// Lazy load below-the-fold components
const Features = lazy(() => import('./components/Features/Features'));
const FeaturesGrid = lazy(() => import('./components/FeaturesGrid/FeaturesGrid'));
const Pricing = lazy(() => import('./components/Pricing/Pricing'));
const ImportExport = lazy(() => import('./components/ImportExport/ImportExport'));
const FinalCTA = lazy(() => import('./components/FinalCTA/FinalCTA'));
const Footer = lazy(() => import('./components/Footer/Footer'));

// Wrapper component to detect when lazy content has loaded
interface LazyContentWrapperProps {
  children: React.ReactNode;
  onLoad: () => void;
}

const LazyContentWrapper: React.FC<LazyContentWrapperProps> = ({ children, onLoad }) => {
  useEffect(() => {
    // Signal that lazy content has mounted
    onLoad();
  }, [onLoad]);

  return <>{children}</>;
};

const App: React.FC = () => {
  const [lazyContentLoaded, setLazyContentLoaded] = useState(false);

  // Manual scroll restoration for lazy-loaded content
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Save scroll position before page unload
    const handleBeforeUnload = () => {
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle hash scrolling and scroll position restoration after lazy content loads
  useEffect(() => {
    if (!lazyContentLoaded) return;

    // Check for hash fragment first (e.g., from 404 page links like /#features)
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.slice(1); // Remove the #
      
      // Use setTimeout to ensure DOM is fully painted after lazy load
      // requestAnimationFrame alone isn't enough for lazy-loaded content
      const scrollToHash = () => {
        const element = document.getElementById(targetId);
        if (element) {
          // Calculate scroll position accounting for sticky nav
          const nav = document.querySelector('nav');
          const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 0;
          const offset = navHeight + 32; // nav height + padding
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      };
      
      // Small delay to ensure all lazy components are mounted and laid out
      setTimeout(scrollToHash, 100);
      return; // Don't restore saved position if we're scrolling to hash
    }

    // Restore saved scroll position (for back/forward navigation)
    const savedPosition = sessionStorage.getItem('scrollPosition');
    if (savedPosition) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        window.scrollTo({
          top: parseInt(savedPosition, 10),
          behavior: 'instant' as ScrollBehavior,
        });
        // Clear saved position after restoration
        sessionStorage.removeItem('scrollPosition');
      });
    }
  }, [lazyContentLoaded]);

  // Initialize analytics and track page view
  useEffect(() => {
    // Initialize analytics system
    analytics.init();

    // Capture UTM parameters and track page view
    const utmParams = captureUTMParameters();
    analytics.trackPageViewed({
      page: window.location.pathname,
      ...utmParams,
    });

    // Set up scroll tracking for sections
    const cleanupScrollTracking = trackSectionViews(analytics);

    // Cleanup on unmount
    return () => {
      cleanupScrollTracking();
    };
  }, []);

  return (
    <ThemeModeProvider>
      <ThemeProvider>
        <div className="app">
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>

          <Header />

          <main id="main-content" role="main" className="main-content" tabIndex={-1}>

            <section id="hero">
              <Hero />
            </section>
            
            <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
              <LazyContentWrapper onLoad={() => setLazyContentLoaded(true)}>
                <section id="features">
                  <Features />
                </section>
                <section id="features-grid">
                  <FeaturesGrid />
                </section>
                <Pricing />
                <section id="demo">
                  <ImportExport />
                </section>
                <section id="final-cta">
                  <FinalCTA />
                </section>
              </LazyContentWrapper>
            </Suspense>
        </main>

        <Suspense fallback={<div style={{ minHeight: '200px' }} />}>
          <Footer />
        </Suspense>

        <WebMCPProvider />
      </div>
      </ThemeProvider>
    </ThemeModeProvider>
  );
};

export default App;

